import useFormMethodsContext from '@src/shared/form/useFormMethodsContext';
import DateRangeInput, {
  type DateRangeInputProps,
  type DateRangeValue,
} from '@src/shared/ui/inputs/DateRangeInput';
import type { Control, FieldValues } from 'react-hook-form';
import { Controller } from 'react-hook-form';

type DateRangeFieldProps = Omit<
  DateRangeInputProps,
  'from' | 'onApply' | 'onClear' | 'onClose' | 'to'
> & {
  name: string;
};

const EMPTY_RANGE: DateRangeValue = { from: null, to: null };

function normalizeValue(value: unknown): DateRangeValue {
  if (!value || typeof value !== 'object') return EMPTY_RANGE;
  const range = value as Partial<DateRangeValue>;
  return {
    from: typeof range.from === 'string' ? range.from : null,
    to: typeof range.to === 'string' ? range.to : null,
  };
}

function DateRangeField({ name, ...inputProps }: DateRangeFieldProps) {
  const { control } = useFormMethodsContext() as { control: Control<FieldValues> };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const value = normalizeValue(field.value);

        return (
          <DateRangeInput
            {...inputProps}
            inputRef={field.ref}
            name={field.name}
            from={value.from}
            to={value.to}
            error={Boolean(fieldState.error) || inputProps.error}
            helperText={fieldState.error?.message || inputProps.helperText}
            onApply={field.onChange}
            onClear={() => field.onChange(EMPTY_RANGE)}
            onClose={field.onBlur}
          />
        );
      }}
    />
  );
}

DateRangeField.displayName = 'DateRangeField';
export default DateRangeField;
